import * as React from "react";
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  styled,
  Switch,
  Tooltip,
  Select,
} from "@mui/material";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";
import {
  SectionTitle,
  SectionBox,
  MessageTextButtons,
  MessageTextArea,
  DefaultTemplateButton,
  ClinicFormSettings,
  TemplateName,
  TemplateDialogProps,
} from "./clinicCustomization";
import {
  DEFAULT_EMAIL_INLINE_TEMPLATE,
  DEFAULT_SMS_INLINE_TEMPLATE,
} from "fe-common/communicationDefaults";
import { ReactComponent as EmailIcon } from "../../../assets/clinic/emailIcon.svg";
import { ReactComponent as SMSIcon } from "../../../assets/clinic/smsIcon.svg";
import CodeIcon from "@mui/icons-material/Code";
import { useRef, useState } from "react";
import { ClinicFormInput } from "../elements/clinicFormInput";
import { SupportedLanguages } from "@kahunmedical/common-configuration";
import { InfoBox } from "../elements/infoBox";
import { useClinicState } from "../../../clinic/clinicStateContext";

//region [[ Styles ]]

const ModalitySwitch = styled("div")({
  display: "flex",
  alignItems: "center",
  "&:not(:first-child)": {
    marginTop: 10,
  },
  "& label": {
    display: "flex",
    gap: 10,
    alignItems: "center",
    minWidth: 70,
  },
  "& svg": {
    width: 26,
    height: 26,
  },
  "&.closed": {
    borderBottom: `1px solid ${ClinicColors.borders}`,
  },
});

const InsertMacroButton = styled(Button)({
  textTransform: "none",
  fontWeight: 400,
  "& svg": {
    width: 18,
    height: 18,
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface MacroField {
  macro: string;
  text: string;
  unsupported?: TemplateName[];
}

export interface ClinicCustomizationGeneralProps {
  settings: ClinicFormSettings;
  clinicDisplayName: string;
  handleSettingsChange: (updated: ClinicFormSettings) => void;
  handleNameChange: (updated: string) => void;
  handelTemplateReset: (template: TemplateDialogProps) => void;
  handleLocaleChange: (newLocale: string) => void;
  handleClinicLocaleChange: (newLocale: string) => void;
  supportedLanguages: string[];
  rtlView: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicCustomizationGeneral = ({
  settings,
  handleSettingsChange,
  handleNameChange,
  handelTemplateReset,
  ...props
}: ClinicCustomizationGeneralProps) => {
  const MacroFields: MacroField[] = [
    {
      macro: "{{patient_first_name}}",
      text: "Patient First Name",
    },
    {
      macro: "{{patient_last_name}}",
      text: "Patient Last Name",
    },
    {
      macro: "{{patient_full_name}}",
      text: "Patient Full Name",
    },
    { macro: "{{visit_date}}", text: "Visit Date & Time" },
    { macro: "{{{case_url}}}", text: "Chat url" },
    { macro: "{{clinic_name}}", text: "Clinic Name" },
    {
      macro:
        '<a href="{{case_url}}" target="_blank"><strong>Click here to start</strong></a>',
      text: "Link to Chat",
      unsupported: [TemplateName.SMSTemplate],
    },
  ];
  const [currentTemplate, setCurrenTemplate] = useState<{
    templateName: TemplateName;
    textareaRef: React.MutableRefObject<any>;
  }>(null);
  const [marcoSelector, setMacroSelector] = useState<HTMLElement>(null);

  const smsTextAreaRef = useRef(null);
  const emailTextAreaRef = useRef(null);

  const closeMacroSelector = () => {
    setMacroSelector(null);
  };

  const toggleSms = () => {
    handleSettingsChange({ ...settings, smsEnabled: !settings.smsEnabled });
  };
  const toggleEmail = () => {
    handleSettingsChange({ ...settings, emailEnabled: !settings.emailEnabled });
  };
  const toggleShowCreationDate = () => {
    handleSettingsChange({
      ...settings,
      showCreationDate: !settings.showCreationDate,
    });
  };
  const toggleShowVisitDate = () => {
    handleSettingsChange({
      ...settings,
      showVisitDate: !settings.showVisitDate,
    });
  };

  const insertMacroField = (macroString: string) => {
    const caretPosition = currentTemplate.textareaRef.current.selectionStart;
    const currentVal = settings[currentTemplate.templateName] || "";
    handleSettingsChange({
      ...settings,
      [currentTemplate.templateName]:
        currentVal.substring(0, caretPosition) +
        macroString +
        currentVal.substring(caretPosition, currentVal.length),
    });
    closeMacroSelector();
  };

  return (
    <div>
      <SectionBox>
        <ClinicFormInput
          label="Clinic Name"
          id="clinicName"
          value={props.clinicDisplayName}
          onChange={(e) => handleNameChange(e.target.value)}
          error={!props.clinicDisplayName && "Clinic name is required"}
          placeHolder={"Enter a name"}
        />
        <div
          className={"language-wrapper"}
          style={{ display: "flex", flexFlow: "row" }}
        >
          <ClinicFormInput
            label="Default Language"
            id="defaultLanguage"
            customInput={
              <>
                <Select
                  id="defaultLanguageSelect"
                  value={settings.defaultLocale}
                  variant={"outlined"}
                  onChange={(e) =>
                    props.handleLocaleChange(e.target.value as string)
                  }
                >
                  {props.supportedLanguages?.map((lang) => (
                    <MenuItem value={lang} key={lang}>
                      {lang}
                    </MenuItem>
                  ))}
                </Select>
                <InfoBox
                  style={{ alignSelf: "end", paddingBottom: 8, paddingLeft: 6 }}
                >
                  Determines the chatbot and messaging language
                </InfoBox>
              </>
            }
          />

          <ClinicFormInput
            label="Summary Language"
            id="defaultLanguage"
            customInput={
              <>
                <Select
                  id="clinicManageLocale"
                  value={settings.clinicManageLocale}
                  variant={"outlined"}
                  onChange={(e) =>
                    props.handleClinicLocaleChange(e.target.value as string)
                  }
                >
                  {props.supportedLanguages // Currently we allow only english and hebrew backoffice and summary
                    ?.filter((l) => l.startsWith("en") || l.startsWith("he"))
                    .map((lang) => (
                      <MenuItem value={lang} key={lang}>
                        {lang}
                      </MenuItem>
                    ))}
                </Select>
                <InfoBox
                  style={{ alignSelf: "end", paddingBottom: 8, paddingLeft: 6 }}
                >
                  Determines the summary and management page language
                </InfoBox>
              </>
            }
          />
        </div>
        <Divider orientation={"horizontal"} style={{ margin: "26px 0" }} />
        <SectionTitle>Sending Modalities</SectionTitle>
        <ModalitySwitch
          className={"modality-switch" + (settings.smsEnabled ? "" : " closed")}
        >
          <label htmlFor={"smsModality"}>
            <SMSIcon /> SMS
          </label>
          <Tooltip
            title={(settings.smsEnabled ? "Disable " : "Enable ") + " SMS"}
            placement="top"
          >
            <Switch
              checked={settings.smsEnabled}
              onClick={() => toggleSms()}
              id="smsModality"
            />
          </Tooltip>
          {settings.smsEnabled && (
            <MessageTextButtons>
              <DefaultTemplateButton
                variant={"text"}
                size={"small"}
                id="useDefaultSmsTemplate"
                onClick={() =>
                  handelTemplateReset({
                    methodName: "SMS",
                    templateName: TemplateName.SMSTemplate,
                    templateDefault: DEFAULT_SMS_INLINE_TEMPLATE,
                  })
                }
                disabled={settings.smsTemplate === DEFAULT_SMS_INLINE_TEMPLATE}
              >
                Use default
              </DefaultTemplateButton>
              <Divider orientation="vertical" />
              <InsertMacroButton
                variant={"text"}
                onClick={(e) => {
                  setMacroSelector(e.currentTarget);
                  setCurrenTemplate({
                    templateName: TemplateName.SMSTemplate,
                    textareaRef: smsTextAreaRef,
                  });
                }}
                endIcon={<CodeIcon />}
                size={"small"}
              >
                Insert Macro Field
              </InsertMacroButton>
            </MessageTextButtons>
          )}
        </ModalitySwitch>
        {settings.smsEnabled && (
          <>
            <MessageTextArea
              id="smsTemplate"
              value={settings.smsTemplate}
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              inputRef={smsTextAreaRef}
              onChange={(e) =>
                handleSettingsChange({
                  ...settings,
                  smsTemplate: e.target?.value,
                })
              }
              inputProps={{
                style: { direction: props.rtlView ? "rtl" : "inherit" },
              }}
            />
          </>
        )}

        <ModalitySwitch
          className={
            "modality-switch" + (settings.emailEnabled ? "" : " closed")
          }
        >
          <label htmlFor={"emailModality"}>
            <EmailIcon />
            Email
          </label>
          <Tooltip
            title={(settings.emailEnabled ? "Disable " : "Enable ") + " Email"}
            placement="top"
          >
            <Switch
              checked={settings.emailEnabled}
              onClick={() => toggleEmail()}
              id="emailModality"
            />
          </Tooltip>
          {settings.emailEnabled && (
            <MessageTextButtons>
              <DefaultTemplateButton
                variant={"text"}
                size={"small"}
                id="useDefaultEMailTemplate"
                onClick={() =>
                  handelTemplateReset({
                    methodName: "Email",
                    templateName: TemplateName.EmailTemplate,
                    templateDefault: DEFAULT_EMAIL_INLINE_TEMPLATE,
                  })
                }
                disabled={
                  settings.emailTemplate === DEFAULT_EMAIL_INLINE_TEMPLATE
                }
              >
                Use default
              </DefaultTemplateButton>
              <Divider orientation="vertical" />
              <InsertMacroButton
                variant={"text"}
                size={"small"}
                onClick={(e) => {
                  setMacroSelector(e.currentTarget);
                  setCurrenTemplate({
                    templateName: TemplateName.EmailTemplate,
                    textareaRef: emailTextAreaRef,
                  });
                }}
                endIcon={<CodeIcon />}
              >
                Insert Macro Field
              </InsertMacroButton>
            </MessageTextButtons>
          )}
        </ModalitySwitch>
        {settings.emailEnabled && (
          <MessageTextArea
            id="emailTemplate"
            value={settings.emailTemplate}
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            inputRef={emailTextAreaRef}
            onChange={(e) =>
              handleSettingsChange({
                ...settings,
                emailTemplate: e.target.value,
              })
            }
            inputProps={{
              style: { direction: props.rtlView ? "rtl" : "inherit" },
            }}
          />
        )}

        <Divider orientation={"horizontal"} style={{ margin: "26px 0" }} />
        <SectionTitle>Patients dashboard</SectionTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
          <div>
            <label htmlFor={"showCreationDate"}>Show creation date</label>
            <Tooltip
              title={
                (settings.showCreationDate ? "Hide " : "Show ") +
                " creation date and time in the patients dashboard"
              }
              placement="top"
            >
              <Switch
                checked={settings.showCreationDate}
                onClick={() => toggleShowCreationDate()}
                id="showCreationDate"
              />
            </Tooltip>
          </div>
          <div>
            <label htmlFor={"showVisitDate"}>Show visit date</label>
            <Tooltip
              title={
                (settings.showVisitDate ? "Hide " : "Show ") +
                " visit date and time in the patients dashboard"
              }
              placement="top"
            >
              <Switch
                checked={settings.showVisitDate}
                onClick={() => toggleShowVisitDate()}
                id="showVisitDate"
              />
            </Tooltip>
          </div>
        </div>
        <Menu
          id="macro-fields-select"
          anchorEl={marcoSelector}
          keepMounted
          open={Boolean(marcoSelector)}
          onClose={() => closeMacroSelector()}
        >
          {MacroFields.filter(
            (mf) => !mf.unsupported?.includes(currentTemplate?.templateName),
          ).map((field, index) => (
            <MenuItem
              onClick={() => {
                insertMacroField(field.macro);
              }}
              key={"macroField" + index}
            >
              {field.text}
            </MenuItem>
          ))}
        </Menu>
      </SectionBox>
    </div>
  );
};
